// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import {
    Grid,
    GridCol,
    Translate,
} from '@plesk/ui-library';
import { PlanCard } from 'common/components/plan/components/PlanCard';
import { PageSubHeader } from 'client/common/components';
import InfiniteScroll from 'common/components/InfinityScroll/InfinityScroll';
import { RootState } from 'client/core/store';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import { loadPlansOnScroll } from 'common/modules/plan/actions';
import { connect } from 'react-redux';
import { IPlanResponse } from 'common/api/resources/Plan';
import { SIZE } from 'common/constants';
import { CustomPlanCard } from 'common/components/plan/components/CustomPlanCard';
import { Dialog } from 'common/components/Dialog/Dialog';
import PlanForm,
{
    FIELDS,
    InitialPlan,
    PlanRequest,
} from 'common/components/plan/PlanForm';
import { ComputeResourceVmCustomPlanRequest } from 'common/api/resources/ComputeResourceVm';
import {
    convertToDataUnit,
    DataUnit,
} from 'common/helpers/units';
import { initialCustomPlanState } from 'common/modules/computeResourceVm/reducers';
import { filterPlanFields } from 'common/helpers/PlanFields';
import { IProjectResponse } from 'common/api/resources/Project';

export const CUSTOM_PLAN = 'custom_plan';

export interface IPlanListProps {
    onItemClicked: (plan: IPlanResponse) => void;
    selectedPlan?: number | typeof CUSTOM_PLAN;
    hidePlanName?: boolean;
    customPlan?: ComputeResourceVmCustomPlanRequest;
    canCustomizePlan?: boolean;
    onCustomizePlan?: (customPlan: ComputeResourceVmCustomPlanRequest) => void;
    minDisk?: number;
    fields?: FIELDS[];
    project: IProjectResponse;
}

export type PlanListProps =
    IPlanListProps &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

export const PlanList: React.FC<PlanListProps> = ({
    hasNextPage,
    plans,
    project,
    selectedPlan,
    onItemClicked,
    loadOnScroll,
    hidePlanName,
    canCustomizePlan,
    customPlan,
    onCustomizePlan,
    minDisk,
    fields = [
        FIELDS.VIRTUALIZATION_TYPE,
        FIELDS.STORAGE_TYPE,
        FIELDS.IMAGE_FORMAT,
        FIELDS.IS_SNAPSHOTS_ENABLED,
        FIELDS.SNAPSHOT_LIMITS,
        FIELDS.DISK,
        FIELDS.RAM,
        FIELDS.VCPU,
        FIELDS.VCPU_UNITS,
        FIELDS.VCPU_LIMIT,
        FIELDS.IO_PRIORITY,
        FIELDS.SWAP,
        FIELDS.IS_BACKUP_AVAILABLE,
        FIELDS.BACKUP_LIMIT,
        FIELDS.INCREMENTAL_BACKUP,
        FIELDS.IS_ADDITIONAL_IP_AVAILABLE,
        FIELDS.DISK_LIMITS,
        FIELDS.NETWORK_LIMITS,
        FIELDS.VZ_PARAMETERS,
        FIELDS.ADDITIONAL_DISK_LIMIT,
        FIELDS.TOKENS_COLUMNS,
        FIELDS.ADDITIONAL_IP_TOKENS,
        FIELDS.BACKUP_PRICE,
        FIELDS.ISO_IMAGE_TOKENS,
        FIELDS.AVAILABLE_APPLICATIONS,
        FIELDS.AVAILABLE_OS_IMAGE_VERSIONS,
        FIELDS.IMAGE_PRESET_IDS,
        FIELDS.IS_DISASTER_RECOVERY_AVAILABLE,
        FIELDS.DISASTER_RECOVERY_PRICE,
    ],
}) => {
    const [initialPlan, setInitialPlan] = React.useState<IPlanResponse>();
    const [customizingPlan, setCustomizingPlan] = React.useState<ComputeResourceVmCustomPlanRequest>({
        ...initialCustomPlanState,
        params: {
            ...initialCustomPlanState.params,
            ram: convertToDataUnit(initialCustomPlanState.params.ram, DataUnit.MiB),
            swap: initialCustomPlanState.params.swap
                ? convertToDataUnit(initialCustomPlanState.params.swap, DataUnit.MiB)
                : 0,
        },
    });
    const [isOpen, setIsOpen] = React.useState(false);

    const handleCustomize = (plan: IPlanResponse) => {
        setInitialPlan(plan);
        setCustomizingPlan({
            ...plan,
            params: {
                ...plan.params,
                ram: convertToDataUnit(plan.params.ram, DataUnit.MiB),
                swap: plan.params.swap
                    ? convertToDataUnit(plan.params.swap, DataUnit.MiB)
                    : 0,
            },
            is_custom: true,
            available_os_image_versions: plan.available_os_image_versions?.map(item => item.id) ?? [],
            available_applications: plan.available_applications?.map(item => item.id) ?? [],
            image_preset_ids: plan.image_presets?.map(item => item.id) ?? [],
        });
        setIsOpen(true);
    };

    const handleEditCustomPlan = (plan: ComputeResourceVmCustomPlanRequest) => {
        setInitialPlan(undefined);
        setCustomizingPlan({
            ...plan,
            params: {
                ...plan.params,
                ram: convertToDataUnit(plan.params.ram, DataUnit.MiB),
                swap: plan.params.swap
                    ? convertToDataUnit(plan.params.swap, DataUnit.MiB)
                    : 0,
            },
        });
        setIsOpen(true);
    };

    const handleSubmitCustomization = (plan: PlanRequest) => {
        if (onCustomizePlan !== undefined) {
            onCustomizePlan({
                ...plan,
                is_custom: true,
            });
        }
        setIsOpen(false);
    };

    return (
        <>
            <PageSubHeader title="servers.create.package"/>
            <InfiniteScroll
                loadMore={loadOnScroll}
                hasMore={hasNextPage}
            >
                <Grid
                    style={{ marginBottom: '-24px' }}
                    gap={SIZE.LG}
                    xs={1}
                    sm={2}
                    md={3}
                    lg={4}
                    xl={5}
                >
                    {canCustomizePlan && (
                        <GridCol>
                            <CustomPlanCard
                                plan={customPlan}
                                isSelected={selectedPlan === CUSTOM_PLAN}
                                onItemClicked={handleEditCustomPlan}
                            />
                        </GridCol>
                    )}
                    {plans.map((plan, key) => (
                        <GridCol key={key}>
                            <PlanCard
                                onItemClicked={onItemClicked}
                                canCustomizeItem={canCustomizePlan}
                                onCustomize={handleCustomize}
                                isSelected={plan.id === selectedPlan}
                                plan={plan}
                                project={project}
                                showName={!hidePlanName}
                            />
                        </GridCol>
                    ))}
                </Grid>
            </InfiniteScroll>
            <Dialog
                heading={<Translate content="servers.create.plan.dialog.title"/>}
                closeHandler={() => setIsOpen(false)}
                isOpen={isOpen}
                size={SIZE.XS}
            >
                <PlanForm
                    onSubmit={handleSubmitCustomization}
                    initialState={{
                        ...customizingPlan,
                        available_applications: [],
                        available_os_image_versions: [],
                        available_locations: [],
                    } as InitialPlan}
                    dataUnit={DataUnit.MiB}
                    submitButton={<Translate content="plan.actionDialog.applyBtn" />}
                    minDisk={minDisk}
                    fields={filterPlanFields(fields, initialPlan)}
                />
            </Dialog>
        </>
    );
};

const mapStateToProps = (state: RootState) => ({
    hasNextPage: !!state.plan.list.links.next,
    plans: state.plan.list.data,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    loadOnScroll: bindActionCreators(loadPlansOnScroll, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(PlanList);
